import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react'

import { type OrderSideNavComparatorGroup, type OrderSideNavSection } from '@/components/order/OrderSideNav'
import {
  comparatorConsentAnyApplicable,
  incidentalConsentGated,
  resolveConsentInteractionGates,
  type ComparatorParentConsentConfigSlice,
  type ConsentInteractionGates,
} from '@/components/order/comparatorParentConsentConfig'
import { getMaxParentSamplesForCategory, type UiConfig } from '@/config/uiConfig'
import { type CanonicalOrderPayload, type Comparator } from '@/ordering/canonicalOrder'
import { getParentComparator } from '@/ordering/comparatorReadAdapter'
import {
  ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS,
  COMPARATOR_RELATIONSHIP_OPTIONS,
  formatComparatorRelationshipLabel,
  isUniqueComparatorRelationship,
  relationshipOptionsForStrategy,
} from '@/ordering/comparatorRelationshipOptions'
import { parentSideForComparator } from '@/ordering/comparatorWriteAdapter'
import { genComparatorId } from '@/ordering/genComparatorId'
import { useAppDispatch } from '@/store/hooks'
import { ensureComparatorIds, upsertComparator } from '@/store/slices/canonicalOrderSlice'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'

/** Anchor id for the proband Incidental Findings / Consent card. */
export const PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID = 'proband-incidental-findings-section'

/**
 * CSS custom property holding the current sticky-top offset (px) for the R5
 * stacked layout: the measured height of the sticky page header plus a small
 * gap. The sticky left nav and the per-person right-column headers pin at
 * `top: var(--order-sticky-top)` so they always clear the real (dynamic)
 * header — no hardcoded pixel guess that breaks when the header wraps or the
 * status banner reflows.
 */
export const ORDER_STICKY_TOP_VAR = '--order-sticky-top'

/**
 * CSS custom property holding the sticky page-header height in px WITHOUT the
 * extra gap. The per-person right-column headers pin at this offset so they sit
 * flush against the page header's bottom edge; combined with their own opaque
 * background + top padding, no card content can peek through above them.
 */
export const ORDER_HEADER_HEIGHT_VAR = '--order-header-h'

/**
 * Measure the sticky page header (`[data-sticky-page-header]`) and publish its
 * height + gap as {@link ORDER_STICKY_TOP_VAR} on the given element (defaults to
 * `document.documentElement`). Re-measures on resize and whenever the header
 * itself changes size (ResizeObserver), so the sticky nav / person headers pin
 * flush beneath it. Pages without a sticky header fall back to `gap` px.
 *
 * @param enabled only wire up when the stacked side-nav layout is active
 * @param gap extra px below the header (default 16)
 */
export function useOrderStickyTop(enabled: boolean, gap = 16): void {
  useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined') return
    const root = document.documentElement
    let ro: ResizeObserver | null = null
    let observedHeader: HTMLElement | null = null

    const apply = () => {
      const header = document.querySelector<HTMLElement>('[data-sticky-page-header]')
      const h = header?.getBoundingClientRect().height ?? 0
      // Only publish once we have a real measurement. A transient 0 (effect runs
      // before the sticky header has laid out) would otherwise pin the nav at
      // just `gap` px — on top of the header. Keep the CSS default (6rem) until
      // a real height is available.
      if (h > 0) {
        root.style.setProperty(ORDER_STICKY_TOP_VAR, `${Math.round(h + gap)}px`)
        // Header height with NO gap: the per-person right-column headers pin
        // flush against the page header's bottom edge (and cover upward with
        // their own background) so cards can't show through a gap above them.
        root.style.setProperty(ORDER_HEADER_HEIGHT_VAR, `${Math.round(h)}px`)
      }
      // Attach the ResizeObserver the first time the header is actually present
      // (it may not exist yet on the initial effect run before first paint).
      if (header && header !== observedHeader && typeof ResizeObserver !== 'undefined') {
        ro?.disconnect()
        ro = new ResizeObserver(apply)
        ro.observe(header)
        observedHeader = header
      }
    }

    // Measure now, again after first paint, after a short settle, and on
    // resize + scroll. The layered attempts cover the case where the sticky
    // header reports height 0 on the initial synchronous run.
    apply()
    const raf = window.requestAnimationFrame(apply)
    const t1 = window.setTimeout(apply, 100)
    const t2 = window.setTimeout(apply, 400)
    window.addEventListener('resize', apply)
    window.addEventListener('scroll', apply, { passive: true })
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.removeEventListener('resize', apply)
      window.removeEventListener('scroll', apply)
      ro?.disconnect()
      root.style.removeProperty(ORDER_STICKY_TOP_VAR)
      root.style.removeProperty(ORDER_HEADER_HEIGHT_VAR)
    }
  }, [enabled, gap])
}

/**
 * TRF Section J physical comparator row ceiling (three rows: J1/J2/J3).
 * This is the absolute physical limit — the TRF renderer cannot fit more rows
 * than this, regardless of the tenant's catalog.
 */
export const TRF_COMPARATOR_ROW_CEILING = 3

/**
 * Whether the R5 side-nav comparator experience is enabled. In production this
 * is solely the `orderSideNav` feature flag; in DEV a `?sidenav=1` query param
 * previews it. Shared by OrderStart and OrderInProgress so the two pages gate
 * the feature identically.
 *
 * @param orderSideNavFlag `config.featureFlags?.orderSideNav`
 * @param sidenavParam the `sidenav` query-string value (e.g. `searchParams.get('sidenav')`)
 */
export function isOrderSideNavEnabled(orderSideNavFlag: boolean | undefined, sidenavParam: string | null): boolean {
  const queryOverride = import.meta.env.DEV && sidenavParam === '1'
  return Boolean(orderSideNavFlag) || queryOverride
}

/**
 * Whether collection-kit ordering is enabled. Gated solely by the `kitOrdering`
 * feature flag (`config.featureFlags?.kitOrdering`). Controls the "Request
 * collection kit" toggle + shipment-address sub-form in the comparator modals
 * (see `ComparatorSideNavModals` / `enableKitRequest`), whose presence is what
 * makes provider-api emit a `kit_shipment.requested` event. Shared by OrderStart
 * and OrderInProgress so both pages gate it identically.
 *
 * @param kitOrderingFlag `config.featureFlags?.kitOrdering`
 */
export function isKitOrderingEnabled(kitOrderingFlag: boolean | undefined): boolean {
  return Boolean(kitOrderingFlag)
}

export interface BuildSideNavSectionsParams {
  requireClinicalSummary: boolean
  requirePhenotypes: boolean
  familyHistoryRequired: boolean
  showGuardianContact: boolean
  /** Whether a canonical order exists (consent section only applies when it does). */
  hasCanonical: boolean
  comparatorConsentConfigs: ComparatorParentConsentConfigSlice
  /**
   * Current canonical payload, used to derive each section's `completed` flag so
   * the side nav drops the brand accent bar once a required section is done.
   * When omitted, sections are left without a completion state (bars behave as
   * required/optional only).
   */
  canonical?: CanonicalOrderPayload | null
}

/**
 * Whether the proband consent ("Additional Options") section is complete.
 * Each secondary consent (incidental, NY sample-retention, research-contact)
 * requires an explicit selection ONLY when its config is authored
 * `requireInteraction: true` (passed via {@link ConsentInteractionGates},
 * already ANDed with applicability) — otherwise it never blocks completeness.
 */
function probandConsentComplete(
  canonical: CanonicalOrderPayload | null | undefined,
  gates: ConsentInteractionGates
): boolean {
  if (gates.incidentalRequireInteraction) {
    const sel = canonical?.patient?.incidentalFindingsSelection
    if (sel !== 'opt-in' && sel !== 'opt-out') return false
  }
  if (gates.nyRetentionRequireInteraction) {
    const ny = canonical?.patient?.newYorkSampleRetentionOptIn
    if (ny !== true && ny !== false) return false
  }
  if (gates.researchContactRequireInteraction) {
    const research = canonical?.patient?.researchContactConsent
    if (research !== true && research !== false) return false
  }
  return true
}

/**
 * R5 side-nav patient section list. Mirrors the rendered patient cards so the
 * scroll-spy anchors line up. Shared by OrderStart and OrderInProgress so both
 * pages stay in sync.
 */
export function buildSideNavSections(params: BuildSideNavSectionsParams): OrderSideNavSection[] {
  const c = params.canonical
  const hasPhenotypes = Array.isArray(c?.reason?.clinicalDiagnosis) && c.reason.clinicalDiagnosis.length > 0
  const sections: OrderSideNavSection[] = [
    // Sequencing Options is always required: it's the core order action.
    {
      id: 'order-section-sequencing',
      label: 'Sequencing Options',
      required: true,
      completed: Boolean(c?.order?.selectedOption?.trim()),
    },
    {
      id: 'order-section-clinical',
      label: 'Clinical Summary',
      required: params.requireClinicalSummary,
      completed: Boolean(c?.clinical?.clinicalNote?.trim()),
    },
    {
      id: 'order-section-indications',
      label: 'Indications',
      required: params.requirePhenotypes,
      completed: hasPhenotypes,
    },
    {
      id: 'order-section-family-history',
      label: 'Family History',
      required: params.familyHistoryRequired,
      completed: Boolean(c?.family?.familyHistory?.trim()),
    },
  ]
  // Consent ("Additional Options") comes before Guardian Contact: the proband's
  // consent choices (research-contact opt-in / incidental findings) determine
  // whether Guardian Contact is required, so consent is collected first.
  if (params.hasCanonical && comparatorConsentAnyApplicable(params.comparatorConsentConfigs)) {
    // "Additional Options" is required whenever proband incidental-findings
    // consent is org-applicable (mirrors the brand "required" accent on the
    // Additional Options card). This section collects optional lab programs;
    // it is not the patient's full genetic-testing consent (that is completed
    // separately in the Inflection Family app).
    sections.push({
      id: PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID,
      label: 'Additional Options',
      required: true,
      completed: probandConsentComplete(c, resolveConsentInteractionGates(params.comparatorConsentConfigs)),
    })
  }
  if (params.showGuardianContact) {
    sections.push({ id: 'order-section-guardian', label: 'Guardian Contact' })
  }
  return sections
}

/**
 * R5 side-nav comparator detail groups: one collapsible group per saved
 * comparator (with a stable client id), each exposing the three required
 * sub-sections (Demographics, Clinical History, Consent).
 */
export function buildComparatorGroups(
  enabled: boolean,
  comparators: ReadonlyArray<Comparator | null | undefined> | null | undefined,
  incidentalApplicable = false,
  mrnRequired = false,
  probandResearchContactConsent = false,
  consentInteractionGates: ConsentInteractionGates = {
    incidentalRequireInteraction: false,
    nyRetentionRequireInteraction: false,
    researchContactRequireInteraction: false,
  }
): OrderSideNavComparatorGroup[] {
  if (!enabled) return []
  const rows = (comparators ?? []).filter(
    (c): c is Comparator => c != null && c.relationship !== 'PROBAND' && typeof c.id === 'string' && c.id.trim() !== ''
  )
  // Count each relationship so labels only get a "1, 2, …" suffix when the same
  // relationship appears more than once (e.g. two siblings → "Sibling 1",
  // "Sibling 2"; a lone aunt stays "Aunt (maternal)"). Numbering follows the
  // stored order so it stays stable as long as the list order does.
  const relationshipTotals = new Map<string, number>()
  for (const c of rows) {
    const key = String(c.relationship ?? '')
    relationshipTotals.set(key, (relationshipTotals.get(key) ?? 0) + 1)
  }
  const relationshipSeen = new Map<string, number>()
  return rows.map((c) => {
    const id = c.id as string
    const relationshipKey = String(c.relationship ?? '')
    const baseLabel = formatComparatorRelationshipLabel(c.relationship) || 'Comparator'
    const total = relationshipTotals.get(relationshipKey) ?? 1
    const ordinal = (relationshipSeen.get(relationshipKey) ?? 0) + 1
    relationshipSeen.set(relationshipKey, ordinal)
    const label = total > 1 ? `${baseLabel} ${ordinal}` : baseLabel
    // Each sub-section clears its brand accent on its own terms, so only the
    // section actually missing data shows purple (matches the detail-view
    // cards, which use the same per-section predicates).
    const demographicsComplete = FormCompletionChecker.comparatorDemographicsComplete(c, {
      mrnRequired,
      probandResearchContactConsent,
    })
    const clinicalComplete = FormCompletionChecker.comparatorClinicalComplete(c)
    const consentComplete = FormCompletionChecker.comparatorConsentComplete(c, {
      incidentalApplicable,
      nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
      researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
    })
    return {
      id,
      label,
      // The group header aggregates its sub-sections: incomplete if ANY
      // required sub-section is outstanding, so a collapsed comparator still
      // flags work on the proband page.
      completed: demographicsComplete && clinicalComplete && consentComplete,
      subSections: [
        {
          id: `comparator-${id}-demographics`,
          label: 'Demographics',
          required: true,
          completed: demographicsComplete,
        },
        { id: `comparator-${id}-clinical`, label: 'Clinical History', required: true, completed: clinicalComplete },
        {
          id: `comparator-${id}-consent`,
          label: 'Additional Options',
          required: true,
          completed: consentComplete,
        },
      ],
    }
  })
}

/** Which focused section editor (within the comparator detail view) is open. */
export type ComparatorSectionGroup = 'demographics' | 'clinical' | 'consent'

export interface ComparatorSectionEditState {
  id: string
  group: ComparatorSectionGroup
}

export interface UseOrderSideNavControllerParams {
  /** R5 side-nav feature gate (production: `featureFlags.orderSideNav`; DEV: `?sidenav=1`). */
  sideNavEnabled: boolean
  /** Current canonical order payload from the slice. */
  storedCanonical: CanonicalOrderPayload | null | undefined
  /** Org-applicable proband consent configs (drives the patient Consent section). */
  comparatorParentConsentConfigs: ComparatorParentConsentConfigSlice
  /** Validation flags used by the section list. */
  requireClinicalSummary: boolean
  requirePhenotypes: boolean
  familyHistoryRequired: boolean
  /** Whether the Guardian Contact section anchor should appear in the nav. */
  showGuardianContact: boolean
  /**
   * Whether MRN counts toward a comparator's completeness (drives the side-nav
   * required accent). Post-submit (OrderInProgress) requires MRN for selected
   * parents; the draft flow (OrderStart) leaves it optional. Defaults to false.
   */
  comparatorMrnRequired?: boolean
  /**
   * Page-specific persistence callback for the slim "Add Comparator" save.
   * Receives the comparator with a guaranteed stable id. Implementations:
   *   - OrderStart (draft): runs the SKU engine for parent adds, then upserts.
   *   - OrderInProgress (submitted): upsert-only (tier moves through the
   *     gated upgrade-only sequencing modal, not the side-nav add).
   */
  onAddComparatorSave: (comparator: Comparator) => void
  /**
   * Fulfillment strategy for the order. When `'inflection_test'`, the
   * "Add Comparator" relationship picker is narrowed to MOTHER / FATHER only
   * (LSMC / SeqOne supports proband + parent comparators only). Defaults to
   * the `rcigm`-safe full list when omitted.
   */
  fulfillmentStrategy?: 'rcigm' | 'inflection_test'
  /**
   * Resolved UI config (required for the two-bound comparator cap). Pass the
   * active config from `useUiConfig()`.
   */
  config: UiConfig
  /**
   * Selected category for the current order (e.g. "Rapid Whole Genome Sequencing (rWGS)").
   * Drives the tenant-derived comparator cap. Extracted from the selected test type's category.
   */
  selectedCategory: string | undefined
}

export interface UseOrderSideNavControllerResult {
  /** id of the comparator whose detail view is shown in the second column (or `null` for patient). */
  selectedComparatorId: string | null
  setSelectedComparatorId: Dispatch<SetStateAction<string | null>>
  /** The selected comparator, enriched from the legacy mother/father slot when applicable. */
  selectedComparator: Comparator | null
  /**
   * Resolve any comparator row by id with the same parent-slot enrichment as
   * `selectedComparator`. Used by the stacked layout to render one detail view
   * per comparator.
   */
  resolveComparatorById: (id: string) => Comparator | null
  /**
   * Numbered display label for the selected comparator (e.g. "Sibling 2" when
   * multiple siblings exist), matching the side-nav group label. Null when no
   * comparator is selected. Pass to `ComparatorDetailView`'s `titleLabel`.
   */
  selectedComparatorLabel: string | null
  /** True when the side-nav is on AND a comparator is selected. */
  showComparatorDetail: boolean
  /** Ref attached to the detail-view wrapper so it can be scrolled into view. */
  comparatorDetailRef: RefObject<HTMLDivElement>

  /** Side-nav patient section list (sequencing/clinical/indications/family-history/...). */
  sideNavSections: OrderSideNavSection[]
  /** One nav group per saved non-proband comparator. */
  comparatorGroups: OrderSideNavComparatorGroup[]
  /**
   * True when adding another comparator is blocked by either Bound 1 (testing tier cap)
   * or Bound 2 (total people / TRF row cap). When true, `comparatorLimitReachedReason`
   * holds the specific blocking reason to surface to the user.
   */
  comparatorLimitReached: boolean
  /**
   * User-facing reason why adding a comparator is blocked, or `null` when it is allowed.
   * Only meaningful when `comparatorLimitReached` is `true`.
   */
  comparatorLimitReachedReason: string | null
  /** The comparator currently flagged as primary guardian, if any. */
  primaryGuardianComparator: Comparator | null
  /**
   * Whether the primary-guardian comparator (if any) is complete on its own
   * terms — the same predicate the side-nav group + submit gate use. The
   * patient Guardian Contact card mirrors this comparator, so when it's
   * incomplete (for ANY reason, not just a missing research-contact channel)
   * the card shows the needs-attention state. `true` when there is no primary
   * guardian (nothing to flag).
   */
  primaryGuardianComparatorComplete: boolean
  /** "Add Comparator" relationship picker options with already-used relationships filtered out. */
  addComparatorRelationshipOptions: typeof COMPARATOR_RELATIONSHIP_OPTIONS

  comparatorSectionEdit: ComparatorSectionEditState | null
  setComparatorSectionEdit: Dispatch<SetStateAction<ComparatorSectionEditState | null>>
  addComparatorOpen: boolean
  setAddComparatorOpen: Dispatch<SetStateAction<boolean>>
  patientConsentModalOpen: boolean
  setPatientConsentModalOpen: Dispatch<SetStateAction<boolean>>

  /** Save a focused section edit (Demographics / Clinical / Consent). Closes the editor. */
  handleComparatorSectionSave: (updated: Comparator) => void
  /**
   * Save the slim Add-Comparator form: assigns a stable id (if missing),
   * delegates persistence to the page-specific `onAddComparatorSave`, then
   * closes the modal and selects the new comparator.
   */
  handleAddComparatorSave: (updated: Comparator) => void
}

/**
 * Shared R5 "Order Side Nav" controller. Centralizes the duplicated
 * comparator-detail state, memos, effects, and handlers used by OrderStart
 * (draft) and OrderInProgress (submitted). Page-specific lifecycle code
 * (session+autosave+submit vs order+baselines+Send-Updates) and editability
 * gates stay on each page; this hook owns only the comparator side-nav UI.
 */
export function useOrderSideNavController(params: UseOrderSideNavControllerParams): UseOrderSideNavControllerResult {
  const {
    sideNavEnabled,
    storedCanonical,
    comparatorParentConsentConfigs,
    requireClinicalSummary,
    requirePhenotypes,
    familyHistoryRequired,
    showGuardianContact,
    comparatorMrnRequired = false,
    onAddComparatorSave,
    fulfillmentStrategy,
    config,
    selectedCategory,
  } = params

  const dispatch = useAppDispatch()

  const [selectedComparatorId, setSelectedComparatorId] = useState<string | null>(null)
  // Set only when a comparator should be scrolled into view (e.g. just added via
  // the Add Comparator modal). Distinct from `selectedComparatorId`, which now
  // tracks the in-view comparator via scroll-spy and therefore changes on every
  // scroll — using it to drive scrolling would fight the user's own scrolling.
  const [pendingScrollComparatorId, setPendingScrollComparatorId] = useState<string | null>(null)
  const [addComparatorOpen, setAddComparatorOpen] = useState(false)
  const [comparatorSectionEdit, setComparatorSectionEdit] = useState<ComparatorSectionEditState | null>(null)
  const [patientConsentModalOpen, setPatientConsentModalOpen] = useState(false)

  // R5: backfill stable client ids on any comparator hydrated without one so
  // the side-nav groups + detail view + section editors can key on `id`
  // (parents projected from the legacy mother/father slots may arrive with
  // only `demographicId` or nothing). Idempotent; runs once per change.
  useEffect(() => {
    if (!sideNavEnabled) return
    const comps = storedCanonical?.comparators
    if (!Array.isArray(comps)) return
    const needsId = comps.some((c) => c != null && !(typeof c.id === 'string' && c.id.trim() !== ''))
    if (needsId) dispatch(ensureComparatorIds())
  }, [sideNavEnabled, storedCanonical?.comparators, dispatch])

  // Incidental is gated for completeness only when applicable AND authored
  // `requireInteraction: true` (consistent with NY / research). When the flag
  // is absent an untouched incidental selection does not block completeness.
  const incidentalApplicable = incidentalConsentGated(comparatorParentConsentConfigs)
  const probandResearchContactConsent = storedCanonical?.patient?.researchContactConsent === true
  const consentInteractionGates = useMemo(
    () => resolveConsentInteractionGates(comparatorParentConsentConfigs),
    [comparatorParentConsentConfigs]
  )

  const sideNavSections = useMemo(
    () =>
      buildSideNavSections({
        requireClinicalSummary,
        requirePhenotypes,
        familyHistoryRequired,
        showGuardianContact,
        hasCanonical: Boolean(storedCanonical),
        comparatorConsentConfigs: comparatorParentConsentConfigs,
        canonical: storedCanonical,
      }),
    [
      requireClinicalSummary,
      requirePhenotypes,
      familyHistoryRequired,
      showGuardianContact,
      storedCanonical,
      comparatorParentConsentConfigs,
    ]
  )

  const comparatorGroups = useMemo(
    () =>
      buildComparatorGroups(
        sideNavEnabled,
        storedCanonical?.comparators,
        incidentalApplicable,
        comparatorMrnRequired,
        probandResearchContactConsent,
        consentInteractionGates
      ),
    [
      sideNavEnabled,
      storedCanonical?.comparators,
      incidentalApplicable,
      comparatorMrnRequired,
      probandResearchContactConsent,
      consentInteractionGates,
    ]
  )

  /**
   * GEN-4471: Two-bound comparator cap. Bound 1 (tier/billing) counts testing comparators;
   * Bound 2 (total people / TRF rows) counts all started comparators. Both derive from the
   * tenant catalog, failing closed to today's max of 2 when config is missing or the category
   * is unknown.
   */
  const { comparatorLimitReached, comparatorLimitReachedReason } = useMemo(() => {
    const comps = storedCanonical?.comparators ?? []
    const testingComparators = comps.filter(
      (c) => c != null && c.isTestingComparator !== false && c.relationship !== 'PROBAND'
    )
    const totalStartedComparators = comps.filter((c) => c != null && c.relationship !== 'PROBAND')

    // Resolve the tenant's category max. When missing/unknown, fail closed to 2 (today's behavior).
    const categoryMax = selectedCategory ? getMaxParentSamplesForCategory(selectedCategory, config) : 0
    const effectiveCategoryMax = categoryMax > 0 ? categoryMax : 2

    const bound1Limit = effectiveCategoryMax
    const bound2Limit = Math.min(effectiveCategoryMax, TRF_COMPARATOR_ROW_CEILING)

    const testingCount = testingComparators.length
    const totalCount = totalStartedComparators.length

    // Bound 1: testing comparators ≤ category max
    if (testingCount >= bound1Limit) {
      return {
        comparatorLimitReached: true,
        comparatorLimitReachedReason: `You can add up to ${bound1Limit} testing comparator${bound1Limit === 1 ? '' : 's'} for this test type. Remove one to add another.`,
      }
    }

    // Bound 2: total started comparators ≤ min(category max, TRF ceiling)
    if (totalCount >= bound2Limit) {
      return {
        comparatorLimitReached: true,
        comparatorLimitReachedReason: `You can add up to ${bound2Limit} comparator${bound2Limit === 1 ? '' : 's'} total. Remove one to add another.`,
      }
    }

    return { comparatorLimitReached: false, comparatorLimitReachedReason: null }
  }, [storedCanonical?.comparators, selectedCategory, config])

  /**
   * R5: when a saved comparator is flagged as the primary guardian, the patient
   * Guardian Contact card mirrors that comparator (read-only) and edits link
   * back to its Demographics — so guardian info has a single source of truth.
   */
  const primaryGuardianComparator = useMemo<Comparator | null>(() => {
    if (!sideNavEnabled) return null
    return (
      (storedCanonical?.comparators ?? []).find(
        (c): c is Comparator =>
          c != null &&
          c.isPrimaryGuardian === true &&
          c.relationship !== 'PROBAND' &&
          typeof c.id === 'string' &&
          c.id.trim() !== ''
      ) ?? null
    )
  }, [sideNavEnabled, storedCanonical?.comparators])

  // The Guardian Contact card mirrors the primary-guardian comparator, so it
  // must surface that comparator's completeness — not just the research-contact
  // channel gap. Uses the same per-comparator predicate (and the same
  // incidental / MRN / proband-research-contact context) as the side-nav group.
  const primaryGuardianComparatorComplete = useMemo<boolean>(() => {
    if (!primaryGuardianComparator) return true
    return FormCompletionChecker.isCanonicalComparatorComplete(primaryGuardianComparator, {
      incidentalApplicable,
      mrnRequired: comparatorMrnRequired,
      probandResearchContactConsent,
      nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
      researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
    })
  }, [
    primaryGuardianComparator,
    incidentalApplicable,
    comparatorMrnRequired,
    probandResearchContactConsent,
    consentInteractionGates,
  ])

  const selectedComparator = useMemo(() => {
    const found = selectedComparatorId
      ? ((storedCanonical?.comparators ?? []).find((c) => c?.id === selectedComparatorId) ?? null)
      : null
    if (!found) return null
    // Plan C (C-read-1): resolve parents through the read adapter so the
    // legacy `mother` / `father` slot enrichment (Sample Type + consent +
    // demographicId) stays at the adapter boundary. This page no longer
    // touches `storedCanonical.mother` / `.father` directly.
    //
    // `getParentComparator` matches by side (first MOTHER / FATHER row), so
    // this returns the same row as the id-selected `found` only because
    // MOTHER / FATHER are a 1-of-1 invariant. `?? found` is a defensive
    // fallback (unreachable while the invariant holds, since `found` already
    // matches the side).
    const side = parentSideForComparator(found)
    if (!side) return found
    return getParentComparator(storedCanonical, side) ?? found
  }, [selectedComparatorId, storedCanonical])

  /**
   * Stacked layout: resolve a comparator row by id with the same parent-slot
   * enrichment as `selectedComparator`, so each stacked `ComparatorDetailView`
   * hydrates Sample Type + consent for parents. Stable across renders for a
   * given `storedCanonical`.
   */
  const resolveComparatorById = useCallback(
    (id: string): Comparator | null => {
      const found = (storedCanonical?.comparators ?? []).find((c) => c?.id === id) ?? null
      if (!found) return null
      const side = parentSideForComparator(found)
      if (!side) return found
      return getParentComparator(storedCanonical, side) ?? found
    },
    [storedCanonical]
  )

  // Numbered display label for the selected comparator, sourced from the same
  // `comparatorGroups` the nav renders so the detail-view header matches (e.g.
  // "Sibling 2" when there are multiple siblings). Null when nothing selected.
  const selectedComparatorLabel = useMemo(() => {
    if (!selectedComparatorId) return null
    return comparatorGroups.find((g) => g.id === selectedComparatorId)?.label ?? null
  }, [selectedComparatorId, comparatorGroups])

  // Drop the selection if the comparator disappears (e.g. removed elsewhere)
  // so the second column never renders a detail view for a missing comparator.
  useEffect(() => {
    if (selectedComparatorId && !selectedComparator) {
      setSelectedComparatorId(null)
    }
  }, [selectedComparatorId, selectedComparator])

  const showComparatorDetail = sideNavEnabled && selectedComparator != null

  // Stacked layout: every comparator's cards are always mounted, so there is no
  // single "detail view" to scroll to on selection. Instead, when a comparator
  // is freshly added we scroll ITS Demographics anchor into view (flush beneath
  // the sticky page header, falling back to native anchor scrolling), then
  // clear the pending id so ordinary scroll-spy selection changes don't scroll.
  const comparatorDetailRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!sideNavEnabled || !pendingScrollComparatorId) return
    if (typeof window === 'undefined') return
    const anchorId = `comparator-${pendingScrollComparatorId}-demographics`
    let raf = 0
    let cancelled = false
    // The Add modal closes with a ~240ms AnimatePresence exit, during which the
    // shared Modal keeps <html> `overflow: hidden` (scroll locked). A scrollTo
    // issued while locked is a no-op, so wait until the lock is released (html
    // overflow no longer 'hidden') before scrolling to the new comparator. Poll
    // via rAF with a safety cap so we never spin if the lock never clears.
    const start = Date.now()
    const attempt = () => {
      if (cancelled) return
      const locked = document.documentElement.style.overflow === 'hidden'
      if (locked && Date.now() - start < 1000) {
        raf = window.requestAnimationFrame(attempt)
        return
      }
      const el = document.getElementById(anchorId)
      if (!el) {
        setPendingScrollComparatorId(null)
        return
      }
      const header = document.querySelector<HTMLElement>('[data-sticky-page-header]')
      if (header) {
        const offset = header.getBoundingClientRect().height + 16
        const top = el.getBoundingClientRect().top + window.scrollY - offset
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      setPendingScrollComparatorId(null)
    }
    raf = window.requestAnimationFrame(attempt)
    return () => {
      cancelled = true
      window.cancelAnimationFrame(raf)
    }
  }, [sideNavEnabled, pendingScrollComparatorId])

  /**
   * R5: persist an edit from one of the comparator detail sections (upsert by
   * id). This is the modal's `onSave`, which autosave also calls on every
   * debounced change, so it must NOT close the editor. Closing happens only
   * via the modal's `onClose` (Done / Discard Changes), keeping the section
   * open while the user is still editing.
   */
  const handleComparatorSectionSave = useCallback(
    (updated: Comparator) => {
      dispatch(upsertComparator(updated))
    },
    [dispatch]
  )

  /**
   * R5: persist a comparator created via the slim Add Comparator modal, then
   * open its detail view. Page-specific persistence runs through
   * `onAddComparatorSave`; this wrapper handles id assignment, modal close,
   * and selection — the bits that are identical for both pages.
   */
  const handleAddComparatorSave = useCallback(
    (updated: Comparator) => {
      const id = updated.id ?? genComparatorId()
      // Incidental findings is never seeded on add — the section requires an
      // explicit provider choice (`requireInteraction`), so a freshly-added
      // comparator keeps `includeIncidentalFindings` null and its Consent
      // section reads incomplete until the provider answers. An explicit
      // user/prior choice is untouched.
      const withId: Comparator = updated.id ? updated : { ...updated, id }
      onAddComparatorSave(withId)
      setAddComparatorOpen(false)
      setSelectedComparatorId(id)
      // Stacked layout: the new comparator's cards mount inline; scroll them
      // into view so the provider lands on what they just added. (Section-EDIT
      // saves intentionally do NOT scroll — only a fresh ADD does.)
      setPendingScrollComparatorId(id)
    },
    [onAddComparatorSave]
  )

  /**
   * R5 "Add Comparator" relationship options. Includes MOTHER / FATHER so a
   * biological parent can be added, and filters out already-used relationships
   * so a duplicate can't be added (MOTHER / FATHER are matched by relationship
   * on upsert, so re-adding would overwrite the existing one). Relationships a
   * patient can legitimately have more than one of — siblings and aunts/uncles
   * (maternal + paternal) — are exempt from the used filter so multiples can be
   * added. LEGAL_GUARDIAN is excluded from the add flow — legal guardians are
   * captured through their own dedicated surface, not as a testing comparator.
   *
   * For `inflection_test` orders the picker is further narrowed to MOTHER /
   * FATHER only (still respecting the used-relationship filter).
   */
  const addComparatorRelationshipOptions = useMemo(() => {
    const used = new Set(
      (storedCanonical?.comparators ?? [])
        .filter((c) => c != null && c.relationship !== 'PROBAND')
        .map((c) => c.relationship)
    )
    const base = relationshipOptionsForStrategy(fulfillmentStrategy, ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS)
    return base.filter(
      (o) => o.value !== 'LEGAL_GUARDIAN' && (!isUniqueComparatorRelationship(o.value) || !used.has(o.value))
    )
  }, [storedCanonical?.comparators, fulfillmentStrategy])

  return {
    selectedComparatorId,
    setSelectedComparatorId,
    selectedComparator,
    resolveComparatorById,
    selectedComparatorLabel,
    showComparatorDetail,
    comparatorDetailRef,
    sideNavSections,
    comparatorGroups,
    comparatorLimitReached,
    comparatorLimitReachedReason,
    primaryGuardianComparator,
    primaryGuardianComparatorComplete,
    addComparatorRelationshipOptions,
    comparatorSectionEdit,
    setComparatorSectionEdit,
    addComparatorOpen,
    setAddComparatorOpen,
    patientConsentModalOpen,
    setPatientConsentModalOpen,
    handleComparatorSectionSave,
    handleAddComparatorSave,
  }
}
